import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useAuthStore from 'store/auth';
import { PATH_ROUTES } from 'routes/path';
import useDashboard from './useDashboard';

export type NavInfo = Record<'title' | 'desc', string>;

const HELPDESK_DETAIL_REGEX = /^\/helpdesk-tickets(?:\/[^\s\/]+)?$/;

const DEFAULT_INFO = { title: '', desc: '' };

export default function useNavInfo() {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const currentMilestone = useAuthStore((store) => store.currentMilestone);
  const { isDashboard } = useDashboard();

  const [navInfo, setNavInfo] = useState<NavInfo>(DEFAULT_INFO);

  const dashboardNavInfo = useMemo(
    () =>
      ({
        'verfication-in-progress': {
          title: t('verification_in_progress'),
          desc: t('wait_for_verification'),
        },
        tnc: {
          title: t('tnc_heading'),
          desc: t('tnc_desc'),
        },
        'user-detail': {
          title: t('user_details_heading'),
          desc: t('user_details_desc'),
        },
      } as const),
    []
  );

  const pageInfo = {
    [PATH_ROUTES.FACE_SCAN]: {
      title: t('face_scan_reports'),
      desc: t('facescan_nav_desc'),
    },
    [PATH_ROUTES.VOICE_SCAN]: {
      title: t('voice_scan_reports'),
      desc: t('voicescan_nav_desc'),
    },
    [PATH_ROUTES.START_VOICE_SCAN]: {
      title: t('voice_scan_analysis'),
      desc: t('voiceanalysis_nav_desc'),
    },
    [PATH_ROUTES.PERSONALISED_AI_REPORT]: {
      title: t('personalised_ai_reports'),
      desc: t('aireport_nav_desc'),
    },
    [PATH_ROUTES.GENERATE_AI_REPORT]: {
      title: t('personalised_ai_reports'),
      desc: t('aireport_nav_desc'),
    },
    [PATH_ROUTES.INTERPRET_LAB_REPORT]: {
      title: t('interpret_lab_reports'),
      desc: t('labreport_nav_desc'),
    },
    [PATH_ROUTES.GENERATE_LAB_REPORT]: {
      title: t('interpret_lab_reports'),
      desc: t('labreport_nav_desc'),
    },
    [PATH_ROUTES.SYMPTOM_CHECKER]: {
      title: t('symptom_checker'),
      desc: t('symptom_checker_nav_desc'),
    },
    [PATH_ROUTES.GENERATE_SYMPTOM_CHECKER]: {
      title: t('symptom_checker'),
      desc: t('symptom_checker_nav_desc'),
    },
    [PATH_ROUTES.RISK_ANALYSIS]: {
      title: t('risk_checker'),
      desc: t('risk_checker_nav_desc'),
    },
    [PATH_ROUTES.ADD_MEMBER]: {
      title: t('add_member'),
      desc: t('add_member_desc'),
    },
    [PATH_ROUTES.HELP_DESK]: {
      title: t('help_desk'),
      desc: t('help_desk_desc'),
    },
    [PATH_ROUTES.COMMUNICATION_PREFERENCE]: {
      title: t('communication_preference'),
      desc: t('communication_preference_desc'),
    },
    [PATH_ROUTES.NOTIFICATION]: {
      title: t('notifications'),
      desc: '',
    },
  } as const;

  useEffect(() => {
    let info;
    if (!isDashboard && pathname === PATH_ROUTES.DASHBOARD) {
      info =
        dashboardNavInfo?.[currentMilestone as keyof typeof dashboardNavInfo];
    } else {
      if (HELPDESK_DETAIL_REGEX.test(pathname)) {
        info = {
          title: t('help_desk'),
          desc: t('help_desk_desc'),
        };
      } else {
        info = pageInfo[pathname];
      }
    }
    setNavInfo(info || DEFAULT_INFO);
  }, [pathname, isDashboard, currentMilestone]);

  return { navInfo };
}
